<script setup>
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useCatalogStore } from '../../../app/store/catalog.js'
import { useAuthStore } from '../../../app/store/auth.js'

const catalogStore = useCatalogStore()
const authStore = useAuthStore()
const { currentViewOptions, validItemsPerRow, isGridMode, isAutoMode } = storeToRefs(catalogStore)

const expanded = ref(authStore.userRole === 'client')
</script>

<template>
  <div class="sidebar-view-settings">
    <div class="settings-header" @click="expanded = !expanded">
      <span class="settings-icon">
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      </span>
      <span class="settings-title">הגדרות תצוגה</span>
      <span class="settings-chevron" :class="{ expanded }">
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </span>
    </div>

    <div v-show="expanded" class="settings-body">
      <!-- Display Mode -->
      <div class="setting-group">
        <label class="setting-label">סוג תצוגה</label>
        <div class="setting-options">
          <button
            class="mode-option"
            :class="{ active: !isGridMode }"
            @click="catalogStore.setDisplayMode('cards')"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <rect x="3" y="3" width="7" height="7" rx="2" />
              <rect x="14" y="3" width="7" height="7" rx="2" />
              <rect x="3" y="14" width="7" height="7" rx="2" />
              <rect x="14" y="14" width="7" height="7" rx="2" />
            </svg>
            <span>כרטיסים</span>
          </button>
          <button
            class="mode-option"
            :class="{ active: isGridMode }"
            @click="catalogStore.setDisplayMode('grid')"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="3" y1="15" x2="21" y2="15" />
              <line x1="9" y1="3" x2="9" y2="21" />
              <line x1="15" y1="3" x2="15" y2="21" />
            </svg>
            <span>רשת</span>
          </button>
        </div>
      </div>

      <!-- Items Per Row -->
      <div class="setting-group">
        <label class="setting-label">פריטים בשורה</label>
        <div class="row-options">
          <button
            class="row-option auto-option"
            :class="{ active: isAutoMode }"
            @click="catalogStore.setAutoItemsPerRow()"
          >
            אוטומטי
          </button>
          <button
            v-for="opt in currentViewOptions"
            :key="opt"
            class="row-option"
            :class="{ active: !isAutoMode && validItemsPerRow === opt }"
            @click="catalogStore.setItemsPerRow(opt)"
          >
            {{ opt }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sidebar-view-settings {
  padding: var(--spacing-md);
  border-top: 1px solid var(--border-color);
}

.settings-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-sm);
  cursor: pointer;
  user-select: none;
}

.settings-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: var(--text-muted);
}

.settings-title {
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex: 1;
}

.settings-chevron {
  display: flex;
  align-items: center;
  color: var(--text-muted);
  transition: transform var(--transition-fast);
  transform: rotate(-90deg);
}

.settings-chevron.expanded {
  transform: rotate(0deg);
}

.settings-body {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.setting-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.setting-label {
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: var(--font-weight-medium);
}

.setting-options {
  display: flex;
  gap: 6px;
}

.mode-option {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 8px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.mode-option:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.mode-option.active {
  background: rgba(99, 102, 241, 0.1);
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.row-options {
  display: flex;
  gap: 2px;
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
  padding: 2px;
  border: 1px solid var(--border-color);
}

.row-option {
  flex: 1;
  padding: 5px 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  color: var(--text-secondary);
  background: transparent;
  border: none;
  border-radius: var(--border-radius-sm);
  cursor: pointer;
  transition: all var(--transition-fast);
  text-align: center;
}

.row-option:hover {
  color: var(--text-primary);
  background: var(--bg-tertiary);
}

.row-option.active {
  color: white;
  background: var(--color-primary);
}

.row-option.auto-option {
  flex: 1.5;
  font-size: 11px;
}
</style>
